import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCENT = "#BACD1C";

function Stat({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="border border-white/10 bg-white/[0.02] p-5"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">
        {label}
      </div>
      <div
        className="mt-2 text-3xl font-bold font-mono"
        style={{ color: ACCENT }}
      >
        {value?.toLocaleString?.() ?? value}
      </div>
    </div>
  );
}

function Bars({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="flex items-end gap-1 h-40" data-testid="analytics-daily-chart">
      {daily.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group">
          <div className="relative w-full flex items-end justify-center h-full">
            <div
              className="w-full transition-all"
              style={{
                height: `${(d.views / max) * 100}%`,
                background: ACCENT,
                opacity: 0.75,
              }}
              title={`${d.day}: ${d.views} views · ${d.visitors} visitors`}
            />
          </div>
          <div className="text-[8px] text-white/30 font-mono rotate-45 origin-left whitespace-nowrap h-6">
            {d.day.slice(5)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AnalyticsAdmin() {
  const [key, setKey] = useState(localStorage.getItem("tq_admin_key") || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/analytics/summary`, {
        headers: { "X-Admin-Key": k },
      });
      setData(data);
      localStorage.setItem("tq_admin_key", k);
      setKey(k);
    } catch (e) {
      if (e?.response?.status === 401) {
        setError("Invalid admin key.");
        localStorage.removeItem("tq_admin_key");
        setKey("");
      } else {
        setError("Could not load analytics. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (key) load(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!key || (!data && !loading)) {
    return (
      <div
        className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6"
        data-testid="analytics-login"
      >
        <div className="w-full max-w-sm border border-white/10 bg-white/[0.02] p-8">
          <div
            className="text-xs uppercase tracking-[0.3em] font-mono mb-1"
            style={{ color: ACCENT }}
          >
            TradrQuant
          </div>
          <h1 className="text-xl font-bold mb-6 font-mono">Traffic Dashboard</h1>
          <label className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono">
            Admin key
          </label>
          <input
            data-testid="admin-key-input"
            type="password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(input.trim())}
            className="mt-2 w-full bg-black border border-white/15 px-3 py-2 font-mono text-sm focus:outline-none focus:border-[#BACD1C]"
            placeholder="enter key"
            autoFocus
          />
          {error && (
            <div className="mt-3 text-xs text-red-400 font-mono" data-testid="admin-error">
              {error}
            </div>
          )}
          <button
            data-testid="admin-login-btn"
            onClick={() => load(input.trim())}
            disabled={loading || !input.trim()}
            className="mt-5 w-full py-2.5 font-mono text-sm font-bold text-black transition-opacity disabled:opacity-40"
            style={{ background: ACCENT }}
          >
            {loading ? "LOADING…" : "VIEW TRAFFIC"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-6 md:p-10" data-testid="analytics-dashboard">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <div
              className="text-xs uppercase tracking-[0.3em] font-mono"
              style={{ color: ACCENT }}
            >
              TradrQuant · Traffic
            </div>
            <h1 className="text-2xl font-bold font-mono mt-1">Site Analytics</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="analytics-refresh-btn"
              onClick={() => load(key)}
              className="text-xs font-mono border border-white/15 px-4 py-2 hover:border-[#BACD1C]"
            >
              {loading ? "…" : "REFRESH"}
            </button>
            <button
              data-testid="analytics-logout-btn"
              onClick={() => {
                localStorage.removeItem("tq_admin_key");
                setKey("");
                setData(null);
              }}
              className="text-xs font-mono text-white/40 hover:text-white"
            >
              LOG OUT
            </button>
          </div>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <Stat label="Total views" value={data.total_views} testid="stat-total-views" />
              <Stat label="Unique visitors" value={data.unique_visitors} testid="stat-unique-visitors" />
              <Stat label="Views today" value={data.views_today} testid="stat-views-today" />
              <Stat label="Visitors today" value={data.uniques_today} testid="stat-uniques-today" />
            </div>

            <div className="mb-10">
              <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-4">
                Views — last {data.daily.length} days
              </div>
              {data.daily.length ? (
                <Bars daily={data.daily} />
              ) : (
                <div className="text-white/30 font-mono text-sm">No data yet.</div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-3">
                  Top pages
                </div>
                <div className="border border-white/10 divide-y divide-white/5" data-testid="top-pages">
                  {data.top_pages.length ? (
                    data.top_pages.map((p) => (
                      <div key={p.path} className="flex justify-between px-4 py-2.5 font-mono text-sm">
                        <span className="text-white/70 truncate mr-3">{p.path}</span>
                        <span style={{ color: ACCENT }}>{p.views}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-white/30 font-mono text-sm">No data yet.</div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono mb-3">
                  Top referrers
                </div>
                <div className="border border-white/10 divide-y divide-white/5" data-testid="top-referrers">
                  {data.top_referrers.length ? (
                    data.top_referrers.map((r) => (
                      <div key={r.referrer} className="flex justify-between px-4 py-2.5 font-mono text-sm">
                        <span className="text-white/70 truncate mr-3">{r.referrer}</span>
                        <span style={{ color: ACCENT }}>{r.views}</span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-white/30 font-mono text-sm">
                      Direct / none yet.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-10 text-[10px] text-white/25 font-mono">
              Updated {new Date(data.updated_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
